import { isDefined } from "@clipboard-health/util-ts";
import type { SxProps, Theme } from "@mui/material/styles";
import { type RecentColleague } from "@src/appV2/Agents/api/useGetRecentColleagues";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useEventCallback } from "@src/appV2/lib/utils/useEventCallback";
import { OpenShiftCardContainer } from "@src/appV2/redesign/Shift/Open/CardContainer";
import { useShiftDiscoveryUserFiltersContext } from "@src/appV2/redesign/ShiftDiscovery/Filters/useUserFiltersContext";
import { SHIFT_DISCOVERY_TRAVEL_FOR_MORE_SHIFTS_FULL_PATH } from "@src/appV2/redesign/ShiftDiscovery/paths";
import { useTravelForMoreShifts } from "@src/appV2/redesign/ShiftDiscovery/TravelForMoreShifts/useTravelForMoreShifts";
import { type WorkerPublicProfile } from "@src/appV2/redesign/WorkWithFriends/hooks/useGetWorkerPublicProfile";
import { useHistory } from "react-router-dom";

import { TravelForMoreShiftsCardView } from "./TravelForMoreShiftsCardView";

const EMPTY_WORKERS: WorkerPublicProfile[] = [];
const EMPTY_FRIENDS_MAP = new Map<string, WorkerPublicProfile>();
const EMPTY_RECENT_COLLEAGUES_MAP = new Map<string, RecentColleague>();

interface TravelForMoreShiftsCardProps {
  readonly sx?: SxProps<Theme>;
}

export function TravelForMoreShiftsCard(props: TravelForMoreShiftsCardProps = {}) {
  const { sx } = props;
  const history = useHistory();
  const { distance } = useShiftDiscoveryUserFiltersContext();
  const isFlagEnabled = useCbhFlag(CbhFeatureFlag.SHIFT_DISCOVERY_TRAVEL_FOR_MORE_SHIFTS_CARD, {
    defaultValue: false,
  });
  const { shifts, workplacesMap, isLoading, isError, isSuccess } = useTravelForMoreShifts({
    enabled: isFlagEnabled,
  });

  const [firstShift] = shifts;
  const firstWorkplace = firstShift
    ? workplacesMap.get(firstShift.relationships.workplace.data.id)
    : undefined;

  const handleCtaClick = useEventCallback(() => {
    logEvent(APP_V2_USER_EVENTS.TRAVEL_FOR_MORE_SHIFTS_CARD_TAPPED);
    history.push(SHIFT_DISCOVERY_TRAVEL_FOR_MORE_SHIFTS_FULL_PATH);
  });

  // Promotional card — hide entirely unless the flag is on and we have a real shift to preview.
  if (
    !isFlagEnabled ||
    isLoading ||
    isError ||
    !isSuccess ||
    !isDefined(firstShift) ||
    !isDefined(firstWorkplace)
  ) {
    return null;
  }

  return (
    <TravelForMoreShiftsCardView
      sx={sx}
      distanceInMiles={distance}
      shiftPreviewSlot={
        <OpenShiftCardContainer
          isFromNearestOpenShifts
          blockingRequirementsContext={undefined}
          friendsMap={EMPTY_FRIENDS_MAP}
          recentColleaguesMap={EMPTY_RECENT_COLLEAGUES_MAP}
          shift={firstShift}
          workers={EMPTY_WORKERS}
          workplace={firstWorkplace}
          onClick={handleCtaClick}
        />
      }
      onCtaClick={handleCtaClick}
    />
  );
}
